import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table"
import { useMemo } from "react"
import {
  cleanColumnName,
  detectColumnType,
  formatDisplayValue,
  type ColumnType,
} from "./dataUtils"

interface DataTableProps {
  data: Array<Record<string, string | number>>
}

export function DataTable({ data }: DataTableProps) {
  const columnTypes = useMemo<Record<string, ColumnType>>(() => {
    if (data.length === 0) return {}
    const first = data[0]
    return Object.fromEntries(
      Object.keys(first).map(k => [k, detectColumnType(k, first[k])]),
    )
  }, [data])

  const columns = useMemo<ColumnDef<Record<string, string | number>>[]>(() => {
    if (data.length === 0) return []
    return Object.keys(data[0]).map(key => ({
      id: key,
      accessorFn: (row: Record<string, string | number>) => row[key],
      header: cleanColumnName(key),
      cell: info =>
        formatDisplayValue(
          info.getValue() as string | number,
          columnTypes[key] ?? "string",
        ),
    }))
  }, [data, columnTypes])

  const MAX_DISPLAY_ROWS = 100

  const displayData = useMemo(
    () =>
      data.length > MAX_DISPLAY_ROWS ? data.slice(0, MAX_DISPLAY_ROWS) : data,
    [data],
  )

  const table = useReactTable({
    data: displayData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (data.length === 0) {
    return <p className="text-sm opacity-50 italic">No data returned</p>
  }

  return (
    <div className="overflow-x-auto max-h-64 my-2">
      <table className="table table-xs table-zebra">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id} className="capitalize text-xs">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} className="text-xs">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > MAX_DISPLAY_ROWS && (
        <p className="text-xs opacity-50 mt-1">
          Showing {MAX_DISPLAY_ROWS} of {data.length} rows
        </p>
      )}
      {data.length > 10 && data.length <= MAX_DISPLAY_ROWS && (
        <p className="text-xs opacity-50 mt-1">
          Showing all {data.length} rows
        </p>
      )}
    </div>
  )
}
